import memoize from "memoize-one"
import { createSelector } from "reselect"
import { getRowData } from "../common/ag-grid-utils"
import { evaluateWorkRules, getObjectId } from "../common/ag-grid-ts-utils"
import { getFlagEnabled } from "../getFlagValue"
import { getCookie } from "../common/ts-utils"

import { projectsSelector } from "./projects"
import { costCodesSelector } from "./cost-codes"

const getRelevantSourceData = (allSourceData, resources) =>
    resources.reduce((acc, resource) => {
        acc[resource] = allSourceData[resource]
        return acc
    }, {})

const memoizedGetRelevantSourceData = memoize(getRelevantSourceData)

const memoizedGetRowData = memoize(getRowData)

export const sourceDataSelector = (state, resources = []) => {
    const allSourceData = state.sourceData?.sourceData
    if (!allSourceData) return null
    return memoizedGetRelevantSourceData(allSourceData, resources)
}

/**
 * This is used to ensure that when rowDataSelector returns an empty array, the
 * Array object reference doesn't change, thus preventing unnecessary re-renders when rowData is empty.
 */
const EMPTY_ROW_DATA_REFERENCE = []

export const rowDataSelector = (state, resources = []) => {
    const sourceData = sourceDataSelector(state, resources)
    return sourceData ? memoizedGetRowData(sourceData) : EMPTY_ROW_DATA_REFERENCE
}

const WORK_RULES_DATA_TYPES = new Set(["timekeepingEntries", "timelineEntryVersions", "ewsStartStopTimes"])

const shouldAnnotateWorkRulesExceptions = (settings, exceptions) =>
    getFlagEnabled("WA-8985-work-rules") &&
    (exceptions?.workRulesException?.enabled || getCookie("workRulesException") === "true") &&
    settings?.tableName === "Weekly Time Card" &&
    settings?.otherSettings?.exceptionFilters?.includes("workRulesException")

const annotateRowDataForWorkRules = (
    rowData,
    referenceableData,
    exceptions,
    settings,
    workRuleSets,
    startOfWeek,
    // tkSummary is also reachable via ``exceptions.tkSummary``; it's listed as an explicit
    // memoize dependency by the wrapper below so a refreshed summary always invalidates the
    // cache, even if some caller were to reuse the surrounding ``exceptions`` reference.
    _tkSummary // eslint-disable-line @typescript-eslint/no-unused-vars
) => {
    if (!shouldAnnotateWorkRulesExceptions(settings, exceptions)) {
        return { rowData, employeesWithExceptions: null }
    }

    if (!Array.isArray(rowData) || !rowData.length) {
        return { rowData, employeesWithExceptions: new Set() }
    }
    if (!referenceableData || !exceptions?.tkSummary || !(workRuleSets?.length || exceptions.workRulesException?.workRuleOverride?.length)) {
        return { rowData, employeesWithExceptions: new Set() }
    }
    const employeesWithExceptions = new Set()
    const annotatedRowData = rowData.map(row => {
        if (row?.dummy || !WORK_RULES_DATA_TYPES.has(row?.dataType)) return row

        const cellSourceData = { [row.dataType]: [row] }
        const { warnings, errors } = evaluateWorkRules(
            exceptions.tkSummary,
            cellSourceData,
            workRuleSets,
            referenceableData,
            startOfWeek,
            undefined, // earlyOut
            undefined, // filterRulesByEntityType
            undefined, // evaluateAllCico
            exceptions.workRulesException?.workRuleOverride
        )
        const workRuleWarningMessages = warnings || []
        const workRuleErrorMessages = errors || []

        if (workRuleWarningMessages.length || workRuleErrorMessages.length) {
            const employeeId = getObjectId(row.employee || row.worker_id)
            if (employeeId) employeesWithExceptions.add(employeeId)
        }

        // Always overwrite workRuleWarnings/workRuleErrors with the freshly-evaluated values.
        // Falling back to the row's existing values (as the prior implementation did) was a
        // staleness trap: any row whose source carried these fields — for example after a
        // paste_cell response — would never reflect a refreshed tkSummary.
        return {
            ...row,
            workRuleWarningMessages,
            workRuleErrorMessages,
            workRuleWarnings: workRuleWarningMessages,
            workRuleErrors: workRuleErrorMessages,
        }
    })

    return { rowData: annotatedRowData, employeesWithExceptions }
}

const memoizedAnnotateRowDataForWorkRules = memoize(annotateRowDataForWorkRules)

/**
 * Filter row data to only include employees with work rules exceptions when the filter is enabled.
 * Returns the original rowData reference when no filtering is required.
 */
export const filterRowDataForWorkRules = (rowData, referenceableData, exceptions, settings, workRuleSets, startOfWeek) => {
    const { rowData: annotatedRowData, employeesWithExceptions } = memoizedAnnotateRowDataForWorkRules(
        rowData,
        referenceableData,
        exceptions,
        settings,
        workRuleSets,
        startOfWeek,
        exceptions?.tkSummary
    )

    if (!employeesWithExceptions || !exceptions?.filterExceptions) return annotatedRowData

    if (!employeesWithExceptions.size) return annotatedRowData.filter(row => row?.dummy)

    return annotatedRowData.filter(row => {
        if (row?.dummy) return true
        const employeeId = getObjectId(row.employee || row.worker_id)
        if (!employeeId) return false
        return employeesWithExceptions.has(employeeId)
    })
}

/**
 * Build a Map<`${dataType}::${gridId}`, annotations> from the annotated rowData. Used by both
 * the cell-renderer's selectedCellData hydration and the toolbar's failOnWorkRuleErrors
 * partition so we don't pay an O(N) walk per cell-focus / per click.
 *
 * Memoized via memoize-one keyed on rowData reference. Reused across consumers within the
 * same render cycle; invalidated when the rowData array reference changes.
 */
const buildWorkRuleAnnotationsByGridKey = rowData => {
    const map = new Map()
    if (!Array.isArray(rowData)) return map
    for (const row of rowData) {
        if (!row || !row.dataType || row.gridId == null) continue
        if (Array.isArray(row.workRuleErrorMessages) || Array.isArray(row.workRuleWarningMessages) || Array.isArray(row.workRuleErrors) || Array.isArray(row.workRuleWarnings)) {
            map.set(`${row.dataType}::${row.gridId}`, {
                workRuleErrorMessages: row.workRuleErrorMessages,
                workRuleWarningMessages: row.workRuleWarningMessages,
                workRuleErrors: row.workRuleErrors,
                workRuleWarnings: row.workRuleWarnings,
            })
        }
    }
    return map
}

export const getWorkRuleAnnotationsByGridKey = memoize(buildWorkRuleAnnotationsByGridKey)

/**
 * These selectors are used to return the referenceable resources associated
 * with a custom table's row data as an array of objects.
 */
export const referenceableIdsSelector = state => state.referenceableIds

export const companyAbsenceTypesSelector = state => state.entities.companyAbsenceTypes?.objects || {}

export const companyClassificationsSelector = state => state.entities.companyClassifications?.objects || {}

export const companyCrewTypesSelector = state => state.entities.companyCrewTypes?.objects || {}

export const companyFormSchemasSelector = state => {
    const relatedSchemas = {}
    // we store bundle schemas in a diff place in the store, add them to the referencable schemas
    // so we can still check them for available status updates
    state.relatedSchemas?.relatedSchemas?.forEach(schema => {
        relatedSchemas[schema.id] = schema
    })
    return { ...state.entities.companyFormSchemas?.objects, ...relatedSchemas } || {}
}

export const companyFormStoresSelector = state => state.entities.companyFormStores?.objects || {}

export const companyGroupsSelector = state => state.entities.companyGroups?.objects || {}

export const companyStartStopTypesSelector = state => state.entities.companyStartStopTypes?.objects || {}

export const companyTradesSelector = state => state.entities.companyTrades?.objects || {}

export const costItemsSelector = state => state.entities.costItems?.objects || {}

export const changeOrderSelector = state => state.entities.changeOrders?.objects || {}

export const employeesSelector = state => state.entities.employees?.objects || {}

export const employeeTradesSelector = state => state.entities.employeeTrades?.objects || {}

export const employeeClassificationsSelector = state => state.entities.employeeClassifications?.objects || {}

export const projectClientNamesSelector = state => state.entities.projectClientNames?.objects || {}

export const guestFormSharesSelector = state => state.entities.guestFormShares?.objects || {}

export const employeeSchemasSelector = state => state.entities.employeeSchemas?.objects || {}

export const equipmentSelector = state => state.entities.equipment?.objects || {}

export const companiesSelector = state => state.entities.companies?.objects || {}

export const analyticsDashboardsSelector = state => state.entities.analyticsDashboards?.objects || {}

export const schemaStatusNamesSelector = state => state.entities.schemaStatusNames?.objects || {}

export const laborRateMatchesSelector = state => state.entities.laborRateMatches?.objects || {}

export const laborRatesSelector = state => state.entities.laborRates?.objects || {}

export const materialsSelector = state => state.entities.materials?.objects || {}

export const projectMaterialsSelector = state => state.entities.projectMaterials?.objects || {}

export const projectEmployeesSelector = state => state.entities.projectEmployees?.objects || {}

export const projectEquipmentSelector = state => state.entities.projectEquipment?.objects || {}

export const equipmentCategoriesSelector = state => state.entities.equipmentCategories?.objects || {}

export const picklistItemsSelector = state => state.entities.picklistItems?.objects || {}

export const timekeepingStatusesSelector = state => state.entities.timekeepingStatuses?.objects || {}

export const timelineEntryVersionsSelector = state => state.entities.timelineEntryVersions?.objects || {}

export const workShiftsSelector = state => state.entities.workShifts?.objects || {}

export const cohortSelector = state => state.entities.cohorts?.objects || {}

export const cohortEmployeesSelector = state => state.entities.cohortEmployees?.objects || {}

export const companyPTOBucketsSelector = state => state.entities.companyPTOBuckets?.objects || {}

/**
 * Selector for work rule sets objects from state.
 */
const workRuleSetsObjectsSelector = state => state.entities?.workRuleSets?.objects || {}

/**
 * Memoized selector that returns work rule sets as an array.
 * Prevents unnecessary recomputation by memoizing the Object.values() transformation.
 */
export const workRuleSetsSelector = createSelector(workRuleSetsObjectsSelector, workRuleSetsObjects => Object.values(workRuleSetsObjects || {}))

/**
 * @returns tEntitySelection
 */
export const referenceableDataSelector = createSelector(
    cohortSelector,
    cohortEmployeesSelector,
    companyAbsenceTypesSelector,
    companyClassificationsSelector,
    companyCrewTypesSelector,
    companyFormSchemasSelector,
    companyFormStoresSelector,
    companyGroupsSelector,
    companyPTOBucketsSelector,
    companyStartStopTypesSelector,
    companyTradesSelector,
    costCodesSelector,
    costItemsSelector,
    changeOrderSelector,
    employeesSelector,
    employeeTradesSelector,
    employeeClassificationsSelector,
    projectClientNamesSelector,
    guestFormSharesSelector,
    laborRateMatchesSelector,
    laborRatesSelector,
    picklistItemsSelector,
    projectEmployeesSelector,
    employeeSchemasSelector,
    equipmentSelector,
    projectEquipmentSelector,
    equipmentCategoriesSelector,
    projectsSelector,
    materialsSelector,
    projectMaterialsSelector,
    companiesSelector,
    schemaStatusNamesSelector,
    analyticsDashboardsSelector,
    timekeepingStatusesSelector,
    timelineEntryVersionsSelector,
    workShiftsSelector,
    workRuleSetsObjectsSelector,
    (
        cohorts,
        cohortEmployees,
        companyAbsenceTypes,
        companyClassifications,
        companyCrewTypes,
        companyFormSchemas,
        companyFormStores,
        companyGroups,
        companyPTOBuckets,
        companyStartStopTypes,
        companyTrades,
        costCodes,
        costItems,
        changeOrders,
        employees,
        employeeTrades,
        employeeClassifications,
        projectClientNames,
        guestFormShares,
        laborRateMatches,
        laborRates,
        picklistItems,
        projectEmployees,
        employeeSchemas,
        equipment,
        projectEquipment,
        equipmentCategories,
        projects,
        materials,
        projectMaterials,
        companies,
        schemaStatusNames,
        analyticsDashboards,
        timekeepingStatuses,
        timelineEntryVersions,
        workShifts,
        workRuleSets
    ) => ({
        cohorts,
        cohortEmployees,
        companyAbsenceTypes,
        companyClassifications,
        companyCrewTypes,
        companyFormSchemas,
        companyFormStores,
        companyGroups,
        companyPTOBuckets,
        companyStartStopTypes,
        companyTrades,
        costCodes,
        costItems,
        changeOrders,
        employees,
        employeeTrades,
        employeeClassifications,
        projectClientNames,
        guestFormShares,
        laborRateMatches,
        laborRates,
        picklistItems,
        projectEmployees,
        employeeSchemas,
        equipment,
        projectEquipment,
        equipmentCategories,
        projects,
        materials,
        projectMaterials,
        companies,
        schemaStatusNames,
        analyticsDashboards,
        timekeepingStatuses,
        timelineEntryVersions,
        workShifts,
        workRuleSets,
    })
)

// Determine whether all the referenceable data for a custom dashboard has
// finished loading
export const referenceableDataForRowDataLoaded = state => {
    const referenceablesLoading = state.referenceablesLoading
    const referenceableResourceIds = state.referenceableIds || {}
    const referenceableResources = Object.keys(referenceableResourceIds)
    return !referenceableResources.find(referenceableResource => {
        return referenceablesLoading[referenceableResource].isLoading === true
    })
}

// Determine whether an error occurred while loading referencable data
export const referenceableDataLoadingError = state => {
    const referenceablesLoading = state.referenceablesLoading
    const referenceableResourceIds = state.referenceableIds || {}
    const referenceableResources = Object.keys(referenceableResourceIds)

    const hasError = referenceableResources.find(referenceableResource => {
        return !!referenceablesLoading[referenceableResource].error
    })

    return hasError ? referenceablesLoading[hasError].error : null
}

// Selector function for the copy To transforms. Placing the logic here to prevent a new array reference
// from being created every time you hover over a cell, or make any changes to the list view
const getRelatedTransforms = state => state.relatedTransforms?.relatedTransforms
export const selectRelatedCopyToTransformsBySchemaIds = createSelector([getRelatedTransforms, (state, schemaIds) => schemaIds], (relatedTransforms, schemaIds) => {
    return relatedTransforms?.filter((transform => transform.from_schemas?.filter(schema => schemaIds.includes(schema.id)) && !transform.is_bundle) || [])
})

// Added a selector for the transformsForImport field mostly for consistency -
// no filtering is needed at this point
export const selectTransformsForImport = state => state.transformsForImport?.transformsForImport

// selector to filter for bundle transforms
export const selectBundleTransforms = createSelector(getRelatedTransforms, relatedTransforms => relatedTransforms?.filter((transform => transform?.is_bundle) || []))

// Retrieve the audit history from state, and make sure that
// each row has a gridId
export const auditHistorySelector = state => {
    const updatedAuditHistory = {}

    // Ensure that each row for each resource in the auditHistory has a gridId
    if (state.auditHistory?.response) {
        Object.entries(state.auditHistory.response).forEach(([key, value]) => {
            updatedAuditHistory[key] = value.map((row, index) => {
                if (!row.gridId) {
                    // The entity's own history ID is the best choice for the gridId.
                    // id won't work, since it will be repeated by historical versions of the same record.
                    // If history ID is absent, fall back to using the index
                    const gridId = row.history_id ? row.history_id : index

                    return { ...row, gridId }
                } else return row
            })
        })
    }

    return updatedAuditHistory
}
